import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus, AlertCircle, Info } from 'lucide-react';
import EnvironmentCard from '../../components/environments/EnvironmentCard';
import Notification from '../../components/common/Notification';
import ApiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const MoneysaverPage = () => {
    const [environments, setEnvironments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [notification, setNotification] = useState(null);
    const [newEnvironment, setNewEnvironment] = useState({
        name: '',
        accountId: '',
        region: 'us-east-1',
        roleArn: '',
        dbInstanceId: ''
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { isSuperUser } = useAuth();

    useEffect(() => {
        if (location.state?.notification) {
            setNotification(location.state.notification);
            navigate(location.pathname, { replace: true });
        }
    }, [location, navigate]);

    useEffect(() => {
        fetchEnvironments();
    }, []);

    const fetchEnvironments = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await ApiService.listEnvironments();
            setEnvironments(response.data || []);
        } catch (error) {
            console.error('Error fetching environments:', error);
            setError(`Failed to load environments: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateClick = () => {
        setShowCreateModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewEnvironment(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        try {
            setLoading(true);

            await ApiService.createEnvironment(newEnvironment);
            await fetchEnvironments();
            setShowCreateModal(false);

            setNotification({
                type: 'success',
                message: `Environment "${newEnvironment.name}" created successfully.`
            });

            navigate(`/moneysaver/environment/${newEnvironment.name}`);
        } catch (error) {
            console.error('Error creating environment:', error);
            setError(`Failed to create environment: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Moneysaver</h1>
                    <p className="text-gray-600">Manage and schedule your AWS resources across multiple accounts</p>
                </div>

                {/* Create Environment Button - visible to super users only */}
                <button
                    onClick={handleCreateClick}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm flex items-center"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    New Environment
                </button>
            </div>

            {/* Display notification if present */}
            {notification && (
                <Notification
                    type={notification.type}
                    message={notification.message}
                    onClose={() => setNotification(null)}
                />
            )}

            {!isSuperUser && (
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <Info className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                For demo purposes, all users can create environments. In a production deployment,
                                only users with the Super User role would have this privilege.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border-l-4 border-red-400 p-4 mt-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <AlertCircle className="h-5 w-5 text-red-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                            <button
                                onClick={fetchEnvironments}
                                className="mt-2 px-3 py-1 text-sm text-white bg-red-500 hover:bg-red-600 rounded"
                            >
                                Retry
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Environment Overview</h2>
                    <div className="flex space-x-4">
                        <span className="flex items-center text-sm">
                            <span className="h-2 w-2 rounded-full bg-green-500 mr-1"></span>
                            Ready
                        </span>
                        <span className="flex items-center text-sm">
                            <span className="h-2 w-2 rounded-full bg-yellow-500 mr-1"></span>
                            Transitioning
                        </span>
                        <span className="flex items-center text-sm">
                            <span className="h-2 w-2 rounded-full bg-gray-500 mr-1"></span>
                            Off
                        </span>
                    </div>
                </div>

                {environments.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {environments.map(env => (
                            <EnvironmentCard key={env.name} environment={env} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-10">
                        <p className="text-gray-500 mb-4">No environments found.</p>
                        <button
                            onClick={handleCreateClick}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm flex items-center mx-auto"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Create Your First Environment
                        </button>
                    </div>
                )}
            </div>

            {/* Create Environment Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Create New Environment</h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleCreateSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Environment Name*
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newEnvironment.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g., development, staging, production"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        AWS Account ID*
                                    </label>
                                    <input
                                        type="text"
                                        name="accountId"
                                        value={newEnvironment.accountId}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g., 123456789012"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        AWS Region*
                                    </label>
                                    <select
                                        name="region"
                                        value={newEnvironment.region}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="us-east-1">US East (N. Virginia)</option>
                                        <option value="us-east-2">US East (Ohio)</option>
                                        <option value="us-west-1">US West (N. California)</option>
                                        <option value="us-west-2">US West (Oregon)</option>
                                        <option value="eu-west-1">EU (Ireland)</option>
                                        <option value="eu-central-1">EU (Frankfurt)</option>
                                        <option value="ap-northeast-1">Asia Pacific (Tokyo)</option>
                                        <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                                        <option value="ap-southeast-2">Asia Pacific (Sydney)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        IAM Role ARN*
                                    </label>
                                    <input
                                        type="text"
                                        name="roleArn"
                                        value={newEnvironment.roleArn}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g., arn:aws:iam::123456789012:role/MoneyserverRole"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        RDS Instance ID*
                                    </label>
                                    <input
                                        type="text"
                                        name="dbInstanceId"
                                        value={newEnvironment.dbInstanceId}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g., database-1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Create Environment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MoneysaverPage;